// src/components/login/mfa-setup-page.tsx
import { useEffect, useState, FormEvent } from "react";
import { QRCodeSVG } from "qrcode.react";
import { authApi } from "@/api/auth";
import { UpmodeLogo } from "./upmode-logo";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Alert, AlertDescription } from "../ui/alert";
import {
  ShieldCheck, AlertCircle, Loader2, Copy, Check,
  Download, ArrowRight,
} from "lucide-react";

interface MfaSetupPageProps {
  setupToken: string;
  username: string;
  onSetupComplete: (accessToken: string, payload: {
    username: string;
    is_admin: boolean;
    user_type: string;
    full_name?: string | null;
  }) => void;
  rememberMe?: boolean;
}

type Phase = "qr" | "verify" | "backup_codes";

export function MfaSetupPage({
  setupToken,
  username,
  onSetupComplete,
  rememberMe = true,
}: MfaSetupPageProps) {
  const [phase, setPhase] = useState<Phase>("qr");

  const [otpauthUri, setOtpauthUri] = useState<string | null>(null);
  const [secretB32, setSecretB32]   = useState<string | null>(null);
  const [code, setCode]             = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [pendingAccessToken, setPendingAccessToken] = useState<string | null>(null);
  const [pendingPayload, setPendingPayload] = useState<any>(null);

  const [secretCopied, setSecretCopied] = useState(false);
  const [codesCopied, setCodesCopied]   = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg,  setErrorMsg]  = useState("");

  // ─── Step 1: fetch the QR / secret on mount ───
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authApi.mfaSetupInit(setupToken);
        if (cancelled) return;
        setOtpauthUri(res.otpauth_uri);
        setSecretB32(res.secret_b32);
        setIsLoading(false);
      } catch (e: any) {
        if (cancelled) return;
        setErrorMsg(e?.message || "Could not start MFA setup. Try logging in again.");
        setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupToken]);

  // ─── Step 2: verify the first TOTP code ───
  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    setErrorMsg("");

    const cleaned = code.replace(/\D/g, "");
    if (cleaned.length !== 6) {
      setErrorMsg("Enter the 6-digit code from your authenticator app.");
      return;
    }

    setIsLoading(true);
    try {
      const res = await authApi.mfaSetupVerify(setupToken, cleaned);
      setBackupCodes(res.backup_codes || []);
      setPendingAccessToken(res.access_token);
      setPendingPayload({
        username:  res.username,
        is_admin:  res.is_admin,
        user_type: res.user_type || "basic",
        full_name: res.full_name,
      });
      setPhase("backup_codes");
    } catch (e: any) {
      setErrorMsg(
        /invalid code/i.test(String(e?.message))
          ? "That code didn't match. Try the latest 6-digit code from your app."
          : (e?.message || "Verification failed.")
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleFinish = () => {
    if (pendingAccessToken && pendingPayload) {
      onSetupComplete(pendingAccessToken, pendingPayload);
    }
  };

  const copyToClipboard = (text: string, kind: "secret" | "codes") => {
    navigator.clipboard.writeText(text).then(() => {
      if (kind === "secret") {
        setSecretCopied(true);
        setTimeout(() => setSecretCopied(false), 2000);
      } else {
        setCodesCopied(true);
        setTimeout(() => setCodesCopied(false), 2000);
      }
    });
  };

  const downloadBackupCodes = () => {
    const text =
      `Upmode AI — backup codes for ${username}\n` +
      `Generated: ${new Date().toISOString()}\n\n` +
      `Each code can be used once if you lose access to your authenticator app.\n` +
      `Keep these somewhere safe (password manager, encrypted note).\n\n` +
      backupCodes.join("\n") + "\n";
    const blob = new Blob([text], { type: "text/plain" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url;
    a.download = `upmode-backup-codes-${username}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "var(--background)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "clamp(16px, 3vh, 28px) clamp(16px, 3vw, 24px)",
        position: "relative",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `
            radial-gradient(circle at 20% 20%, var(--spectral-violet-5) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, var(--signal-blue-5) 0%, transparent 50%)
          `,
          opacity: 0.6,
          pointerEvents: "none",
        }}
      />

      <div style={{
        width: "100%", maxWidth: 560, position: "relative", zIndex: 1,
        display: "flex", flexDirection: "column", gap: 22,
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ display: "inline-flex", marginBottom: 14 }}>
            <UpmodeLogo />
          </div>
          <h1 style={{
            fontFamily: "var(--font-sans)",
            fontSize: "var(--text-3xl)",
            fontWeight: "var(--font-weight-semibold)",
            color: "var(--foreground)",
            margin: 0,
            letterSpacing: "-0.02em",
          }}>
            {phase === "backup_codes" ? "Save your backup codes" : "Set up two-factor authentication"}
          </h1>
          <p style={{
            marginTop: 8,
            color: "var(--muted-foreground)",
            fontSize: "var(--text-sm)",
          }}>
            {phase === "backup_codes"
              ? "These let you sign in if you lose access to your authenticator."
              : `Required for ${username}. Takes about 60 seconds.`}
          </p>
        </div>

        <div style={{
          backgroundColor: "var(--card)",
          border: "1px solid var(--border)",
          borderRadius: 16,
          padding: "clamp(22px, 3.5vh, 36px)",
          boxShadow: "var(--elevation-lg)",
          position: "relative",
          overflow: "hidden",
        }}>
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, height: 4,
            background: "linear-gradient(90deg, var(--spectral-violet), var(--signal-blue), var(--alpha-teal))",
            borderRadius: "16px 16px 0 0", opacity: 0.8,
          }}/>

          {errorMsg && (
            <Alert variant="destructive" style={{
              marginBottom: 16,
              backgroundColor: "var(--infrared-coral-5)",
              border: "1px solid var(--infrared-coral)",
            }}>
              <AlertCircle style={{ width: 16, height: 16, color: "var(--infrared-coral)" }} />
              <AlertDescription style={{ color: "var(--infrared-coral)" }}>{errorMsg}</AlertDescription>
            </Alert>
          )}

          {/* PHASE: QR + secret */}
          {phase === "qr" && (
            <>
              {isLoading ? (
                <div style={{ textAlign: "center", padding: "32px 0" }}>
                  <Loader2 className="animate-spin" style={{ width: 24, height: 24, color: "var(--muted-foreground)" }} />
                  <p style={{ marginTop: 12, color: "var(--muted-foreground)", fontSize: "var(--text-sm)" }}>
                    Generating your secret...
                  </p>
                </div>
              ) : otpauthUri ? (
                <>
                  <ol style={{
                    paddingLeft: 22, margin: 0, marginBottom: 18,
                    fontSize: "var(--text-sm)", color: "var(--foreground)",
                    lineHeight: 1.7,
                  }}>
                    <li>Open Google Authenticator, 1Password, Authy, or your password manager.</li>
                    <li>Scan this QR code, or paste the secret manually.</li>
                    <li>Enter the 6-digit code it shows you.</li>
                  </ol>

                  <div style={{
                    display: "flex", justifyContent: "center", padding: 16,
                    backgroundColor: "white", borderRadius: 12,
                    border: "1px solid var(--border)",
                    marginBottom: 14,
                  }}>
                    <QRCodeSVG value={otpauthUri} size={180} level="M" />
                  </div>

                  {secretB32 && (
                    <div style={{
                      backgroundColor: "var(--muted)",
                      border: "1px solid var(--border)",
                      borderRadius: 8,
                      padding: "10px 12px",
                      display: "flex", alignItems: "center", gap: 8,
                      marginBottom: 18,
                    }}>
                      <code style={{
                        flex: 1, fontFamily: "var(--font-mono, monospace)",
                        fontSize: "var(--text-sm)", letterSpacing: "0.05em",
                        wordBreak: "break-all",
                        color: "var(--foreground)",
                      }}>
                        {secretB32}
                      </code>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(secretB32, "secret")}
                        style={{
                          background: "none", border: "none", cursor: "pointer",
                          color: "var(--muted-foreground)", padding: 4,
                          display: "flex", alignItems: "center",
                        }}
                        aria-label="Copy secret"
                        title="Copy secret"
                      >
                        {secretCopied
                          ? <Check style={{ width: 16, height: 16, color: "var(--signal-green)" }} />
                          : <Copy style={{ width: 16, height: 16 }} />}
                      </button>
                    </div>
                  )}

                  <Button
                    type="button"
                    onClick={() => setPhase("verify")}
                    style={{
                      width: "100%", height: 48,
                      background: "var(--alpha-teal)", color: "white",
                      fontWeight: 600, fontSize: "var(--text-base)",
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    }}
                  >
                    <span>I've added it — continue</span>
                    <ArrowRight style={{ width: 18, height: 18 }} />
                  </Button>
                </>
              ) : null}
            </>
          )}

          {/* PHASE: enter first TOTP */}
          {phase === "verify" && (
            <form onSubmit={handleVerify}>
              <div style={{
                display: "flex", alignItems: "center", gap: 10, marginBottom: 16,
                color: "var(--foreground)",
              }}>
                <ShieldCheck style={{ width: 20, height: 20, color: "var(--alpha-teal)" }} />
                <p style={{ margin: 0, fontSize: "var(--text-sm)" }}>
                  Enter the 6-digit code from your authenticator app.
                </p>
              </div>

              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                disabled={isLoading}
                placeholder="123 456"
                autoFocus
                style={{
                  height: 56,
                  fontSize: 24,
                  textAlign: "center",
                  letterSpacing: "0.4em",
                  fontFamily: "var(--font-mono, monospace)",
                  borderRadius: "var(--radius)",
                  marginBottom: 16,
                }}
              />

              <Button
                type="submit"
                disabled={isLoading || code.length !== 6}
                style={{
                  width: "100%", height: 48,
                  background: code.length === 6 && !isLoading ? "var(--alpha-teal)" : "var(--muted)",
                  color: "white", fontWeight: 600,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}
              >
                {isLoading
                  ? <Loader2 className="animate-spin" style={{ width: 18, height: 18 }} />
                  : <ShieldCheck style={{ width: 18, height: 18 }} />}
                Verify and finish
              </Button>

              <button
                type="button"
                onClick={() => { setPhase("qr"); setCode(""); setErrorMsg(""); }}
                style={{
                  marginTop: 12, width: "100%",
                  background: "none", border: "none", cursor: "pointer",
                  color: "var(--muted-foreground)", fontSize: "var(--text-sm)",
                }}
              >
                ← Back to QR code
              </button>
            </form>
          )}

          {/* PHASE: show backup codes */}
          {phase === "backup_codes" && (
            <>
              <Alert style={{
                marginBottom: 16,
                backgroundColor: "var(--alpha-teal-5)",
                border: "1px solid var(--alpha-teal-20)",
              }}>
                <ShieldCheck style={{ width: 16, height: 16, color: "var(--alpha-teal)" }} />
                <AlertDescription style={{ color: "var(--foreground)" }}>
                  Two-factor authentication is now active.
                </AlertDescription>
              </Alert>

              <p style={{
                margin: 0, marginBottom: 14,
                fontSize: "var(--text-sm)",
                color: "var(--foreground)",
                lineHeight: 1.6,
              }}>
                Save these <strong>backup codes</strong> now. Each works once if you lose your
                authenticator. They will not be shown again.
              </p>

              <div style={{
                backgroundColor: "var(--muted)",
                border: "1px solid var(--border)",
                borderRadius: 8,
                padding: 16,
                marginBottom: 14,
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
                fontFamily: "var(--font-mono, monospace)",
                fontSize: "var(--text-sm)",
                letterSpacing: "0.05em",
                color: "var(--foreground)",
              }}>
                {backupCodes.map((c, i) => (
                  <div key={i}>{c}</div>
                ))}
              </div>

              <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => copyToClipboard(backupCodes.join("\n"), "codes")}
                  style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
                >
                  {codesCopied
                    ? <Check style={{ width: 14, height: 14, color: "var(--signal-green)" }} />
                    : <Copy style={{ width: 14, height: 14 }} />}
                  Copy
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={downloadBackupCodes}
                  style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
                >
                  <Download style={{ width: 14, height: 14 }} />
                  Download .txt
                </Button>
              </div>

              <Button
                type="button"
                onClick={handleFinish}
                style={{
                  width: "100%", height: 48,
                  background: "var(--alpha-teal)", color: "white",
                  fontWeight: 600,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                }}
              >
                <span>I've saved them — continue to Upmode</span>
                <ArrowRight style={{ width: 18, height: 18 }} />
              </Button>
            </>
          )}
        </div>

        <p style={{
          textAlign: "center", margin: 0,
          fontSize: "var(--text-xs)",
          color: "var(--muted-foreground)",
        }}>
          Lost your authenticator? Contact your administrator to reset MFA.
        </p>
      </div>
    </div>
  );
}